import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button, Card, Badge, SectionHeading } from '../components/shared/SharedComponents';
import { 
  ArrowLeft, RefreshCw, AlertCircle, AlertTriangle 
} from 'lucide-react';
import { reauditService } from '../services/reauditService';
import { buildReauditPayload, renderDiffViewModel } from '../services/diffViewModel';
import { DiffViewModel } from '../types';

const ReauditDiffPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const [viewModel, setViewModel] = useState<DiffViewModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDiff = async () => {
      if (!id) {
        setError('No audit ID provided.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        // 1. Fetch complete stored audit using public ID
        const storedAudit = await reauditService.getStoredAuditByPublicId(id);
        if (!storedAudit) {
          setError('The requested audit was not found or has expired.');
          return;
        }

        // 2. Build the payload and compile view-model
        const diffPayload = buildReauditPayload(storedAudit);
        const model = renderDiffViewModel(diffPayload);
        setViewModel(model);
      } catch (err) {
        console.error('Failed to construct re-audit comparison:', err);
        setError('An unexpected error occurred while compiling the comparison.');
      } finally {
        setIsLoading(false);
      }
    };

    loadDiff();
  }, [id]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-32 text-center space-y-8" role="status" aria-live="polite">
        <div className="flex justify-center">
          <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin" aria-hidden="true" />
        </div>
        <p className="text-muted-foreground font-serif italic">Recomputing historical audit delta...</p>
      </div>
    );
  }

  if (error || !viewModel) {
    return (
      <div className="container mx-auto px-4 py-32 text-center space-y-8 max-w-lg">
        <div className="flex justify-center">
          <div className="w-24 h-24 rounded-full bg-foreground/5 flex items-center justify-center">
            <AlertCircle size={48} className="text-muted-foreground opacity-20" />
          </div>
        </div>
        <div className="space-y-4">
          <h1 className="text-3xl font-serif italic">{error || 'Comparison Failed'}</h1>
          <p className="text-muted-foreground text-sm">
            Please check the audit URL or verify the identifier from your email link.
          </p>
        </div>
        <Link to="/">
          <Button size="md">Return Home</Button>
        </Link>
      </div>
    );
  }

  const {
    oldMonthlySavings,
    newMonthlySavings,
    deltaMonthlySavings,
    deltaAnnualSavings,
    deltaDirection,
    changedRecommendations,
    unchangedRecommendations,
    changedTools,
  } = viewModel;

  const isSavingsImproved = deltaDirection === 'improved';
  const isSavingsDecreased = deltaDirection === 'worsened';

  return (
    <main className="container mx-auto px-6 pt-32 pb-24 max-w-5xl animate-in fade-in duration-700">
      
      {/* Header Back Link */}
      <div className="mb-8">
        <Link to={`/result/${id}`} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft size={16} /> Back to original report
        </Link>
      </div>

      {/* Heading */}
      <div className="space-y-4 mb-16">
        <Badge variant="warning">Re-Audit comparison</Badge>
        <h1 className="text-5xl md:text-6xl font-serif italic tracking-tighter leading-tight">
          Audit Delta Analysis <br />
          <span className="text-muted-foreground opacity-40">Ref: {id.substring(0, 8).toUpperCase()}</span>
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl">
          Deterministic review comparing your original audit against vendor pricing structures active today.
        </p>
      </div>

      {/* Delta Financials Summary Hero */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <Card className="md:col-span-2 bg-foreground text-background overflow-hidden relative p-8">
          <div className="absolute top-0 right-0 w-80 h-80 bg-primary/20 rounded-full blur-3xl -z-10 pointer-events-none" />
          
          <div className="h-full flex flex-col justify-between space-y-8">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground opacity-60 mb-2">Savings Impact</p>
              <h2 className="text-5xl md:text-6xl font-serif italic tracking-tighter">
                {isSavingsImproved ? 'Savings Improved' : isSavingsDecreased ? 'Savings Decreased' : 'Savings Unchanged'}
              </h2>
            </div>
            
            <div className="grid grid-cols-2 gap-8 border-t border-background/10 pt-6">
              <div>
                <p className="text-xs uppercase text-muted-foreground opacity-60 mb-1">Original Savings</p>
                <p className="text-2xl font-bold font-sans">${oldMonthlySavings.toFixed(0)}/mo</p>
              </div>
              <div>
                <p className="text-xs uppercase text-muted-foreground opacity-60 mb-1">Updated Savings</p>
                <p className="text-2xl font-bold font-sans">${newMonthlySavings.toFixed(0)}/mo</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-foreground/5 border-foreground/10 flex flex-col justify-between p-8">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Monthly Variance</p>
            <div className="flex items-baseline gap-2">
              <span className={`text-4xl md:text-5xl font-bold font-sans ${isSavingsImproved ? 'text-green-600' : isSavingsDecreased ? 'text-red-600' : 'text-foreground'}`}>
                {deltaMonthlySavings >= 0 ? '+' : '-'}${Math.abs(deltaMonthlySavings).toFixed(0)}
              </span>
              <span className="text-muted-foreground text-sm">/mo</span>
            </div>
          </div>

          <div className="border-t border-foreground/10 pt-4 mt-6">
            <p className="text-xs text-muted-foreground">Annualized Delta</p>
            <p className="text-lg font-semibold font-sans mt-1">
              {deltaAnnualSavings >= 0 ? '+' : '-'}${Math.abs(deltaAnnualSavings).toFixed(0)}/year
            </p>
          </div>
        </Card>
      </div>

      {/* Changed Vendors List */}
      {changedTools.length > 0 && (
        <Card className="border-amber-500/10 bg-amber-500/5 mb-12 p-8">
          <div className="flex gap-4">
            <AlertTriangle className="text-amber-600 shrink-0 mt-1" size={24} />
            <div className="space-y-4 w-full">
              <div>
                <h3 className="font-serif italic text-lg text-amber-900 dark:text-amber-300">
                  Detected Vendor Price Shifts
                </h3>
                <p className="text-sm text-muted-foreground">
                  The following vendor rate changes triggered this audit recalculation:
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-amber-500/20 text-amber-900 dark:text-amber-300/80 font-semibold">
                      <th className="py-2 pr-4">Vendor & Plan</th>
                      <th className="py-2 px-4">Original Rate</th>
                      <th className="py-2 pl-4">Current Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changedTools.map((change, idx) => (
                      <tr key={idx} className="border-b border-amber-500/10">
                        <td className="py-3 pr-4 font-medium text-foreground">
                          {change.toolName} <span className="text-xs text-muted-foreground">({change.planLabel})</span>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {change.oldPrice !== null ? `$${change.oldPrice}/mo` : 'Custom/Sales'}
                        </td>
                        <td className="py-3 pl-4 font-semibold text-foreground">
                          {change.newPrice !== null ? `$${change.newPrice}/mo` : 'Custom/Sales'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Main Recommendations Comparison Section */}
      <div className="space-y-12">
        <SectionHeading title="Recommendation Comparison" subtitle="Review updated guidance side-by-side with original metrics." align="left" className="mb-8" />

        {/* Changed Recommendations */}
        <div className="space-y-6">
          <h3 className="text-lg font-serif italic border-b border-foreground/10 pb-2">Changed Recommendations</h3>
          {changedRecommendations.length === 0 ? (
            <p className="text-muted-foreground text-sm italic">No structural shifts occurred in recommendation strategies.</p>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {changedRecommendations.map((pair, idx) => {
                const isRemoval = !pair.new;
                const isAddition = !pair.old;

                return (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 rounded-3xl border border-foreground/10 bg-foreground/[0.02]">
                    {/* Left: Original */}
                    <div className="space-y-3 opacity-60">
                      <div className="flex justify-between items-center">
                        <Badge variant="default">Original Strategy</Badge>
                        {pair.old && (
                          <span className="text-sm font-semibold">${pair.old.estimatedSavings}/mo</span>
                        )}
                      </div>
                      {pair.old ? (
                        <div>
                          <h4 className="font-semibold text-foreground">{pair.old.title}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{pair.old.description}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">No recommendation was present.</p>
                      )}
                    </div>

                    {/* Right: Updated */}
                    <div className="space-y-3 border-t md:border-t-0 md:border-l border-foreground/10 pt-6 md:pt-0 md:pl-6">
                      <div className="flex justify-between items-center">
                        <Badge variant={isRemoval ? 'warning' : 'primary'}>
                          {isRemoval ? 'No Longer Applicable' : isAddition ? 'New Opportunity' : 'Updated Guidance'}
                        </Badge>
                        {pair.new && (
                          <span className="text-sm font-bold text-foreground">${pair.new.estimatedSavings}/mo</span>
                        )}
                      </div>
                      {pair.new ? (
                        <div>
                          <h4 className="font-semibold text-foreground flex items-center gap-2">
                            {pair.new.title}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-1">{pair.new.description}</p>
                        </div>
                      ) : (
                        <div>
                          <h4 className="font-semibold text-muted-foreground">Removed Recommendation</h4>
                          <p className="text-xs text-muted-foreground mt-1">This optimization is no longer viable due to current pricing shifts.</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Unchanged Recommendations */}
        {unchangedRecommendations.length > 0 && (
          <div className="space-y-6">
            <h3 className="text-lg font-serif italic border-b border-foreground/10 pb-2">Unchanged Recommendations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {unchangedRecommendations.map((rec, idx) => (
                <div key={idx} className="p-6 rounded-3xl border border-foreground/5 bg-foreground/[0.01] opacity-70">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-[10px] uppercase font-semibold text-muted-foreground tracking-widest">{rec.type}</span>
                    <span className="text-sm font-semibold">${rec.estimatedSavings}/mo</span>
                  </div>
                  <h4 className="font-medium text-foreground text-sm">{rec.title}</h4>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{rec.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA Button Block */}
      <div className="mt-16 border-t border-foreground/10 pt-12 flex flex-col md:flex-row justify-between items-center gap-8">
        <div className="space-y-1 text-center md:text-left">
          <h4 className="font-serif italic text-lg">Initiate updated strategic planning?</h4>
          <p className="text-xs text-muted-foreground">Rerun your configuration profile to capture these new optimal tiers.</p>
        </div>
        <Link to="/audit">
          <Button size="lg" className="shadow-2xl shadow-primary/20">
            Rerun Full AI Audit <RefreshCw size={16} className="ml-1" />
          </Button>
        </Link>
      </div>

    </main>
  );
};

export default ReauditDiffPage;
